import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import icon from 'flarum/common/helpers/icon';
import Sortable from 'sortablejs';
import { compressImage } from '../imageCompression';

const MAX_MEDIA = 9;
const DEFAULT_IMAGE_QUALITY = 0.82;

export default class MediaComposer extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.uploading = false;
    this.compressing = false;
    this.sortable = null;
  }

  oncreate(vnode) {
    super.oncreate(vnode);
    this.createSortable(vnode.dom);
  }

  onupdate(vnode) {
    super.onupdate(vnode);

    if (!this.sortable) this.createSortable(vnode.dom);
  }

  onremove(vnode) {
    if (this.sortable) this.sortable.destroy();
    this.sortable = null;

    const composer = this.attrs.composer;

    if (composer.spacePocketMediaDiscussion && !composer.spacePocketMediaSubmitted) {
      composer.spacePocketMediaDiscussion.pushAttributes({
        spacePocketMedia: JSON.parse(composer.spacePocketOriginalMedia || '[]'),
      });
    }

    super.onremove(vnode);
  }

  createSortable(root) {
    const list = root.querySelector('.SpacePocketPosts-mediaList');

    if (!list) return;

    this.sortable = Sortable.create(list, {
      animation: 160,
      draggable: '.SpacePocketPosts-mediaItem',
      handle: '.SpacePocketPosts-dragHandle',
      ghostClass: 'is-sorting',
      onEnd: ({ oldIndex, newIndex }) => {
        if (oldIndex === undefined || newIndex === undefined || oldIndex === newIndex) return;

        const media = this.media();
        const [moved] = media.splice(oldIndex, 1);
        media.splice(newIndex, 0, moved);
        this.syncDiscussion();
        m.redraw();
      },
    });
  }

  media() {
    if (!Array.isArray(this.attrs.composer.spacePocketMedia)) {
      this.attrs.composer.spacePocketMedia = [];
    }

    return this.attrs.composer.spacePocketMedia;
  }

  syncDiscussion() {
    const discussion = this.attrs.composer.spacePocketMediaDiscussion;

    if (discussion && typeof discussion.pushAttributes === 'function') {
      discussion.pushAttributes({
        spacePocketMedia: this.media().map((item) => ({ ...item })),
      });
    }
  }

  chooseFiles(event) {
    event.preventDefault();
    this.element.querySelector('input[type="file"]').click();
  }

  async upload(event) {
    const input = event.currentTarget;
    const selected = Array.from(input.files || []);
    input.value = '';

    if (!selected.length || this.uploading) return;

    const images = selected.filter((file) => file.type.startsWith('image/'));

    if (images.length !== selected.length) {
      app.alerts.show({ type: 'error' }, app.translator.trans('space-pocket-posts.forum.composer.invalid_file'));
    }

    const remaining = MAX_MEDIA - this.media().length;

    if (remaining <= 0) {
      this.showLimitError();
      return;
    }

    if (images.length > remaining) this.showLimitError();

    const files = images.slice(0, remaining);

    if (!files.length) return;

    this.uploading = true;
    this.attrs.composer.spacePocketMediaUploading = true;

    try {
      let filesToUpload = files;
      const compressionSetting = app.forum.attribute('spacePocketImageCompressionEnabled');
      const compressionEnabled =
        compressionSetting === true ||
        compressionSetting === 1 ||
        compressionSetting === '1' ||
        compressionSetting === 'true';

      if (compressionEnabled) {
        this.compressing = true;
        m.redraw();
        const compressedFiles = [];
        const configuredQuality = Number(app.forum.attribute('spacePocketImageQuality'));
        const imageQuality =
          Number.isFinite(configuredQuality) && configuredQuality >= 0.1 && configuredQuality <= 1
            ? configuredQuality
            : DEFAULT_IMAGE_QUALITY;

        for (const file of files) {
          compressedFiles.push(await compressImage(file, imageQuality));
        }

        filesToUpload = compressedFiles;
        this.compressing = false;
        m.redraw();
      }

      const body = new FormData();
      filesToUpload.forEach((file) => body.append('files[]', file));

      const response = await app.request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/fof/upload',
        serialize: (raw) => raw,
        body,
      });

      const resources = Array.isArray(response.data) ? response.data : [];

      resources.forEach((resource) => {
        const attributes = resource.attributes || {};

        if (!attributes.url || !String(attributes.type || '').startsWith('image/')) return;

        this.media().push({
          url: attributes.url,
          uuid: attributes.uuid || String(resource.id),
          name: attributes.baseName || '',
          type: attributes.type,
        });
      });

      this.syncDiscussion();
    } catch (error) {
      const detail = error && error.response && error.response.errors && error.response.errors[0] && error.response.errors[0].detail;
      app.alerts.show(
        { type: 'error' },
        detail || app.translator.trans('space-pocket-posts.forum.composer.upload_failed')
      );
    } finally {
      this.compressing = false;
      this.uploading = false;
      this.attrs.composer.spacePocketMediaUploading = false;
      m.redraw();
    }
  }

  showLimitError() {
    app.alerts.show(
      { type: 'error' },
      app.translator.trans('space-pocket-posts.forum.composer.max_images', { count: MAX_MEDIA })
    );
  }

  remove(index) {
    this.media().splice(index, 1);
    this.syncDiscussion();
  }

  move(index, offset) {
    const target = index + offset;

    if (target < 0 || target >= this.media().length) return;

    const [item] = this.media().splice(index, 1);
    this.media().splice(target, 0, item);
    this.syncDiscussion();
  }

  view() {
    const media = this.media();
    const canAdd = media.length < MAX_MEDIA && !this.uploading;

    return (
      <div className="SpacePocketPosts-composer">
        <input className="SpacePocketPosts-fileInput" type="file" accept="image/*" multiple onchange={(event) => this.upload(event)} />

        <div className="SpacePocketPosts-mediaList">
          {media.map((item, index) => (
            <div className="SpacePocketPosts-mediaItem" key={item.uuid + '-' + index}>
              <img src={item.url} alt={item.name || app.translator.trans('space-pocket-posts.forum.composer.image_alt')} />

              {index === 0 && <span className="SpacePocketPosts-cover">{app.translator.trans('space-pocket-posts.forum.composer.cover')}</span>}

              <button
                type="button"
                className="SpacePocketPosts-remove Button Button--icon"
                aria-label={app.translator.trans('space-pocket-posts.forum.composer.remove')}
                onclick={() => this.remove(index)}
              >
                {icon('fas fa-times')}
              </button>

              <button
                type="button"
                className="SpacePocketPosts-dragHandle"
                aria-label={app.translator.trans('space-pocket-posts.forum.composer.drag_sort')}
              >
                {icon('fas fa-grip-vertical')}
              </button>

              <div className="SpacePocketPosts-orderActions">
                <button
                  type="button"
                  disabled={index === 0}
                  aria-label={app.translator.trans('space-pocket-posts.forum.composer.move_left')}
                  onclick={() => this.move(index, -1)}
                >
                  {icon('fas fa-chevron-left')}
                </button>
                <button
                  type="button"
                  disabled={index === media.length - 1}
                  aria-label={app.translator.trans('space-pocket-posts.forum.composer.move_right')}
                  onclick={() => this.move(index, 1)}
                >
                  {icon('fas fa-chevron-right')}
                </button>
              </div>
            </div>
          ))}

          {canAdd && (
            <Button
              type="button"
              className="SpacePocketPosts-add"
              icon="fas fa-plus"
              aria-label={app.translator.trans('space-pocket-posts.forum.composer.add_images')}
              onclick={(event) => this.chooseFiles(event)}
            />
          )}

          {this.uploading && (
            <div className="SpacePocketPosts-uploading" role="status">
              {icon('fas fa-spinner', { className: 'fa-spin' })}
              <span>
                {app.translator.trans(
                  this.compressing
                    ? 'space-pocket-posts.forum.composer.compressing'
                    : 'space-pocket-posts.forum.composer.uploading'
                )}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }
}
